<template>
  <!--
    The walkthrough menu (spec 33 section 7.2): a button in the header and the
    popover it opens. Demo only: the header renders it when GET /organization
    carries the `demo` object, so a real tenant never has it in the page.

    The visitor starts every walkthrough from here or from a link (spec 33
    P2: self-chosen tours are the ones people finish). The popover is a
    non-modal dialog: the page behind it stays live, Escape and a click
    outside close it, and focus goes back to the button.

    It also keeps the "Try on your own" checklist (6.6), ticked from where the
    visitor actually goes, which is why it watches the route while mounted.
  -->
  <div ref="root" class="lw-c-tour-menu">
    <button
      ref="trigger"
      type="button"
      class="lw-c-tour-menu__trigger"
      data-tour="demo.walkthroughs"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="panelId"
      @click="toggle"
    >
      Walkthroughs
    </button>

    <section
      v-if="open"
      :id="panelId"
      ref="panel"
      class="lw-c-tour-menu__panel"
      role="dialog"
      aria-modal="false"
      :aria-labelledby="`${panelId}-title`"
      tabindex="-1"
      @keydown.esc.stop="close"
    >
      <h2 :id="`${panelId}-title`" class="lw-c-tour-menu__title">See it work</h2>
      <p class="lw-c-tour-menu__lede">
        Each one runs in your own demo fleet. You do every step yourself; the walkthrough shows you
        where, and what to type.
      </p>

      <ul class="lw-c-tour-menu__list">
        <li v-for="item in walkthroughs" :key="item.id" class="lw-c-tour-menu__item">
          <button
            type="button"
            class="lw-c-tour-menu__start"
            :data-test="`walkthrough-${item.id}`"
            @click="start(item.id)"
          >
            <span class="lw-c-tour-menu__name">
              <span
                class="lw-c-tour-menu__tick"
                :class="{ 'lw-c-tour-menu__tick--done': done.includes(item.id) }"
                aria-hidden="true"
              />
              {{ item.title }}
              <span v-if="done.includes(item.id)" class="lw-c-tour-menu__sr">(done)</span>
            </span>
            <span class="lw-c-tour-menu__meta"
              >{{ item.summary }} About {{ item.minutes }}
              {{ item.minutes === 1 ? 'minute' : 'minutes' }}.</span
            >
          </button>
        </li>
      </ul>

      <h3 class="lw-c-tour-menu__subtitle">Try on your own</h3>
      <ul class="lw-c-tour-menu__checklist" data-test="explore-list">
        <li v-for="item in exploreItems" :key="item.id" class="lw-c-tour-menu__check">
          <span
            class="lw-c-tour-menu__tick"
            :class="{ 'lw-c-tour-menu__tick--done': explored.includes(item.id) }"
            aria-hidden="true"
          />
          <router-link :to="item.to" class="lw-c-tour-menu__link" @click="close">
            {{ item.label }}
          </router-link>
          <span v-if="explored.includes(item.id)" class="lw-c-tour-menu__sr">(done)</span>
        </li>
      </ul>

      <button type="button" class="lw-c-tour-menu__overview" @click="start('sales')">
        Or the three-minute overview
      </button>
    </section>
  </div>
</template>

<script>
import { WALKTHROUGHS } from '../../tours/registry';
import { EXPLORE_ITEMS, itemsVisitedBy } from '../../tours/explore';
import { markExplored, readProgress } from '../../tours/persistence';
import { trackWalkthrough } from '../../tours/analytics';

let uid = 0;

export default {
  name: 'LwTourMenu',
  data() {
    uid += 1;
    return {
      open: false,
      panelId: `lw-tour-menu-${uid}`,
      done: [],
      explored: [],
      walkthroughs: WALKTHROUGHS,
      exploreItems: EXPLORE_ITEMS,
    };
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler(name) {
        this.noteVisit(name);
      },
    },
  },
  mounted() {
    window.addEventListener('lw:walkthroughs-open', this.show);
    document.addEventListener('pointerdown', this.onOutside, true);
    this.refresh();
  },
  beforeUnmount() {
    window.removeEventListener('lw:walkthroughs-open', this.show);
    document.removeEventListener('pointerdown', this.onOutside, true);
  },
  methods: {
    refresh() {
      const progress = readProgress();
      this.done = progress.done;
      this.explored = progress.explored;
    },
    /** Tick what this visit counts for, once each, and say so to Umami. */
    noteVisit(name) {
      const before = readProgress().explored;
      for (const id of itemsVisitedBy(name)) {
        if (before.includes(id)) continue;
        markExplored(id);
        trackWalkthrough('explore', { item: id });
      }
      this.refresh();
    },
    toggle() {
      if (this.open) this.close();
      else this.show();
    },
    show() {
      this.refresh();
      this.open = true;
      this.$nextTick(() => {
        if (this.$refs.panel) this.$refs.panel.focus({ preventScroll: true });
      });
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.$nextTick(() => {
        if (this.$refs.trigger) this.$refs.trigger.focus({ preventScroll: true });
      });
    },
    onOutside(event) {
      if (!this.open) return;
      const root = this.$refs.root;
      if (root && root.contains(event.target)) return;
      this.open = false;
    },
    /** A walkthrough starts the way a link starts it: ?tour= on this page. */
    start(id) {
      this.open = false;
      const route = this.$route || { path: '/', query: {} };
      Promise.resolve(
        this.$router.replace({ path: route.path, query: { ...(route.query || {}), tour: id } })
      ).catch(() => {});
    },
  },
};
</script>

<style scoped>
.lw-c-tour-menu {
  position: relative;
}

.lw-c-tour-menu__trigger {
  appearance: none;
  background: transparent;
  border: 1px solid var(--lw-fg-on-chassis-muted);
  border-radius: var(--lw-radius-pill);
  color: var(--lw-fg-on-chassis);
  cursor: pointer;
  font: var(--lw-type-caption-strong);
  margin-right: var(--lw-space-3);
  padding: var(--lw-space-1) var(--lw-space-3);
  white-space: nowrap;
}

.lw-c-tour-menu__trigger:hover,
.lw-c-tour-menu__trigger:focus-visible {
  border-color: var(--lw-fg-on-chassis);
}

.lw-c-tour-menu__panel {
  background: var(--lw-elev-3-surface);
  border: 1px solid var(--lw-elev-3-line);
  border-radius: var(--lw-radius-md);
  box-shadow: var(--lw-elev-3-shadow);
  color: var(--lw-fg-primary);
  max-height: calc(100vh - var(--lw-space-8));
  max-width: calc(100vw - var(--lw-space-6));
  outline: none;
  overflow-y: auto;
  padding: var(--lw-space-5);
  position: absolute;
  right: 0;
  top: calc(100% + var(--lw-space-2));
  width: 24rem;
  z-index: var(--lw-z-overlay);
}

.lw-c-tour-menu__title {
  font: var(--lw-type-subhead);
  margin: 0 0 var(--lw-space-1);
}

.lw-c-tour-menu__lede {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-body-sm);
  margin: 0 0 var(--lw-space-3);
}

.lw-c-tour-menu__list,
.lw-c-tour-menu__checklist {
  list-style: none;
  margin: 0;
  padding: 0;
}

.lw-c-tour-menu__start {
  appearance: none;
  background: transparent;
  border: 0;
  border-radius: var(--lw-radius-sm);
  color: inherit;
  cursor: pointer;
  display: block;
  padding: var(--lw-space-2);
  text-align: left;
  width: 100%;
}

.lw-c-tour-menu__start:hover,
.lw-c-tour-menu__start:focus-visible {
  background: var(--lw-bg-inset);
}

.lw-c-tour-menu__name {
  align-items: center;
  display: flex;
  font: var(--lw-type-body);
  font-weight: var(--lw-type-caption-strong-weight);
  gap: var(--lw-space-2);
}

.lw-c-tour-menu__meta {
  color: var(--lw-fg-secondary);
  display: block;
  font: var(--lw-type-body-sm);
  margin-left: var(--lw-space-5);
}

.lw-c-tour-menu__subtitle {
  font: var(--lw-type-label);
  letter-spacing: var(--lw-type-label-tracking);
  margin: var(--lw-space-4) 0 var(--lw-space-2);
  text-transform: var(--lw-type-label-transform);
}

.lw-c-tour-menu__check {
  align-items: center;
  display: flex;
  font: var(--lw-type-body-sm);
  gap: var(--lw-space-2);
  padding: var(--lw-space-1) var(--lw-space-2);
}

.lw-c-tour-menu__link {
  color: var(--lw-navy-700);
}

.lw-c-tour-menu__tick {
  border: 1px solid var(--lw-fg-secondary);
  border-radius: var(--lw-radius-pill);
  display: inline-block;
  flex: none;
  height: 0.75rem;
  width: 0.75rem;
}

.lw-c-tour-menu__tick--done {
  background: var(--lw-success-fg);
  border-color: var(--lw-success-fg);
}

.lw-c-tour-menu__overview {
  appearance: none;
  background: transparent;
  border: 0;
  color: var(--lw-navy-700);
  cursor: pointer;
  font: var(--lw-type-body-sm);
  margin-top: var(--lw-space-4);
  padding: 0;
  text-decoration: underline;
}

/* Read by a screen reader, never seen. */
.lw-c-tour-menu__sr {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

/* A phone: the panel is a sheet under the header, full width. */
@media (max-width: 720px) {
  .lw-c-tour-menu__panel {
    left: var(--lw-space-3);
    position: fixed;
    right: var(--lw-space-3);
    top: var(--lw-space-8);
    width: auto;
  }
}
</style>
